import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, RefreshCw, Folder, FileText, Coins, CalendarDays, Users2 } from 'lucide-react'
import { FolderPicker } from '../organize/FolderPicker'
import { DocumentTypePicker } from './DocumentTypePicker'
import { CounterpartyInput } from './CounterpartyInput'
import { InsertFieldMenu } from './InsertFieldMenu'
import { useDepartments, useOrgMembers } from '../../hooks/useOrganization'
import { readContentControls, ccValuesToMetadata } from '../../hooks/useContentControls'
import { devLog } from '../../services/devLog'

export interface DocumentMetadata {
  folder_id?: number | null
  document_type_id?: number | null
  department_id?: number | null
  responsible_person?: string | null
  contract_value?: string | null
  contract_currency?: string | null
  contract_expires_at?: string | null
  counterparties?: string[]
}

const CURRENCIES = ['NOK', 'EUR', 'USD', 'SEK', 'DKK', 'GBP'] as const

interface MetadataPanelProps {
  actorId?: string
  orgKoblinkId?: string
  isExpanded: boolean
  onToggle: () => void
  metadata: DocumentMetadata
  onMetadataChange: (metadata: DocumentMetadata) => void
}

export function MetadataPanel({
  actorId,
  orgKoblinkId,
  isExpanded,
  onToggle,
  metadata,
  onMetadataChange,
}: MetadataPanelProps) {
  const { t } = useTranslation()
  const { data: departments } = useDepartments(orgKoblinkId)
  const { data: members } = useOrgMembers(orgKoblinkId)
  const [isSyncing, setIsSyncing] = useState(false)

  const filledCount = [
    metadata.folder_id != null,
    metadata.document_type_id != null,
    metadata.department_id != null,
    metadata.responsible_person != null,
    metadata.contract_value != null,
    metadata.contract_expires_at != null,
    (metadata.counterparties?.length ?? 0) > 0,
  ].filter(Boolean).length

  const update = (partial: Partial<DocumentMetadata>) => {
    onMetadataChange({ ...metadata, ...partial })
  }

  /** Read Content Controls from Word document and pre-fill panel */
  const syncFromDocument = useCallback(async () => {
    setIsSyncing(true)
    try {
      const ccValues = await readContentControls()
      const ccMetadata = ccValuesToMetadata(ccValues)

      if (Object.keys(ccMetadata).length > 0) {
        // Merge CC values into panel (CC values fill empty panel fields)
        onMetadataChange({
          ...metadata,
          ...(ccMetadata.counterparties && ccMetadata.counterparties.length > 0 && !metadata.counterparties?.length && {
            counterparties: ccMetadata.counterparties,
          }),
          ...(ccMetadata.contract_value != null && !metadata.contract_value && {
            contract_value: ccMetadata.contract_value,
          }),
          ...(ccMetadata.contract_currency != null && !metadata.contract_currency && {
            contract_currency: ccMetadata.contract_currency,
          }),
          ...(ccMetadata.contract_expires_at != null && !metadata.contract_expires_at && {
            contract_expires_at: ccMetadata.contract_expires_at,
          }),
        })
        devLog.info('MetadataPanel', `Synced ${Object.keys(ccMetadata).length} field(s) from document`)
      } else {
        devLog.info('MetadataPanel', 'No KobSign fields found in document')
      }
    } catch (e) {
      devLog.warn('MetadataPanel', `Sync failed: ${e}`)
    } finally {
      setIsSyncing(false)
    }
  }, [metadata, onMetadataChange])

  return (
    <div className="mb-3 border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 px-2.5 py-2 text-[13px] font-semibold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          <span className="flex-1 text-left">{t('metadata.title')}</span>
          {filledCount > 0 && (
            <span className="text-[10px] text-primary-600 dark:text-primary-400 font-normal">
              {t('metadata.filledFields', { count: filledCount })}
            </span>
          )}
        </button>
        {/* Insert field menu + sync button */}
        {isExpanded && (
          <div className="flex items-center gap-1 pr-2">
            <button
              onClick={syncFromDocument}
              disabled={isSyncing}
              className="p-1 rounded text-neutral-400 hover:text-primary-500 transition-colors disabled:opacity-50"
              title={t('contentControls.syncFromDoc')}
            >
              <RefreshCw size={11} className={isSyncing ? 'animate-spin' : ''} />
            </button>
            <InsertFieldMenu />
          </div>
        )}
      </div>

      {/* Collapsed summary — show filled fields as compact chips */}
      {!isExpanded && filledCount > 0 && (
        <button
          onClick={onToggle}
          className="w-full flex flex-wrap items-center gap-1.5 px-2.5 py-2 border-t border-neutral-100 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          {metadata.folder_id != null && (
            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded px-1.5 py-0.5">
              <Folder size={10} className="text-neutral-400" />
              {t('organize.folders')}
            </span>
          )}
          {metadata.document_type_id != null && (
            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded px-1.5 py-0.5">
              <FileText size={10} className="text-neutral-400" />
              {t('metadata.documentType')}
            </span>
          )}
          {metadata.contract_value && (
            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded px-1.5 py-0.5">
              <Coins size={10} className="text-neutral-400" />
              {Number(metadata.contract_value).toLocaleString()} {metadata.contract_currency || 'NOK'}
            </span>
          )}
          {metadata.contract_expires_at && (
            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded px-1.5 py-0.5">
              <CalendarDays size={10} className="text-neutral-400" />
              {metadata.contract_expires_at}
            </span>
          )}
          {(metadata.counterparties?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded px-1.5 py-0.5">
              <Users2 size={10} className="text-neutral-400" />
              {metadata.counterparties!.length === 1 ? metadata.counterparties![0] : `${metadata.counterparties!.length} ${t('metadata.counterparty').toLowerCase()}`}
            </span>
          )}
          <span className="text-[10px] text-primary-500 dark:text-primary-400 ml-auto">
            {t('common.edit')} ▸
          </span>
        </button>
      )}

      {/* Collapsible content */}
      {isExpanded && (
        <div className="px-2.5 pb-2.5 space-y-2.5 border-t border-neutral-100 dark:border-neutral-700 pt-2.5 animate-fade-in">
          {/* Folder */}
          <Field label={t('organize.folders')}>
            <FolderPicker
              actorId={actorId}
              selectedFolderId={metadata.folder_id}
              onSelect={(id) => update({ folder_id: id })}
            />
          </Field>

          {/* Org-specific fields */}
          {orgKoblinkId && (
            <>
              {/* Document type */}
              <Field label={t('metadata.documentType')}>
                <DocumentTypePicker
                  orgKoblinkId={orgKoblinkId}
                  selectedId={metadata.document_type_id}
                  onSelect={(id) => update({ document_type_id: id })}
                />
              </Field>

              {/* Department */}
              {departments && departments.length > 0 && (
                <Field label={t('metadata.department')}>
                  <select
                    value={metadata.department_id ?? ''}
                    onChange={(e) => update({ department_id: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
                  >
                    <option value="">{t('metadata.selectDepartment')}</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </Field>
              )}

              {/* Responsible person */}
              {members && members.length > 0 && (
                <Field label={t('metadata.responsiblePerson')}>
                  <select
                    value={metadata.responsible_person ?? ''}
                    onChange={(e) => update({ responsible_person: e.target.value || null })}
                    className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
                  >
                    <option value="">{t('metadata.selectPerson')}</option>
                    {members.map((m) => (
                      <option key={m.koblink_id} value={m.koblink_id}>{m.display_name}</option>
                    ))}
                  </select>
                </Field>
              )}
            </>
          )}

          {/* Counterparty — chips input with contact search */}
          <Field label={t('metadata.counterparty')}>
            <CounterpartyInput
              values={metadata.counterparties ?? []}
              onChange={(vals) => update({ counterparties: vals.length > 0 ? vals : undefined })}
              actorId={actorId}
            />
          </Field>

          {/* Contract value + currency */}
          <Field label={t('metadata.contractValue')}>
            <div className="flex gap-1.5">
              <input
                type="number"
                value={metadata.contract_value ?? ''}
                onChange={(e) => update({ contract_value: e.target.value || null })}
                placeholder="0.00"
                className="flex-[7] min-w-0 px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
              />
              <select
                value={metadata.contract_currency ?? 'NOK'}
                onChange={(e) => update({ contract_currency: e.target.value })}
                className="flex-[3] min-w-0 px-1 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </Field>

          {/* Expiry date */}
          <Field label={t('metadata.expiryDate')}>
            <input
              type="date"
              value={metadata.contract_expires_at ?? ''}
              onChange={(e) => update({ contract_expires_at: e.target.value || null })}
              className="w-full px-2 py-1.5 text-xs rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-1"
            />
          </Field>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 mb-1 block">
        {label}
      </label>
      {children}
    </div>
  )
}
